import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faInfoCircle, faStar, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Header, Footer, MediaCard, MediaCardSkeleton, HeroSkeleton } from './shared';
import { useTMDB } from '../hooks/useFetch';
import { useConnectionSpeed } from '../hooks';
import { useDevice } from '../hooks/useMediaQuery';
import { IMAGE_SIZES, APP_CONFIG, YOUTUBE_CONFIG } from '../config/constants';
import type { MediaItem } from '../types';
import { getMediaTitle } from '../utils';

const JustFlix: React.FC = () => {
  const navigate = useNavigate();
  const { isMobileOrTablet, prefersReducedMotion } = useDevice();
  const { fetchPopularMovies, fetchTrending, searchMulti, fetchTrailer } = useTMDB();
  const { isFast, tested: speedTested } = useConnectionSpeed();

  // State
  const [movies, setMovies] = useState<MediaItem[]>([]);
  const [sliderMovies, setSliderMovies] = useState<MediaItem[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trailerKey, setTrailerKey] = useState<string | null>(null);
  const [showTrailer, setShowTrailer] = useState(false);

  // Refs to prevent re-fetching and track initial load
  const initialLoadDone = useRef(false);
  const popularMoviesCache = useRef<MediaItem[]>([]);

  // Fetch initial data - runs only once
  useEffect(() => {
    if (initialLoadDone.current) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const [popularMovies, trendingData] = await Promise.all([
          fetchPopularMovies(),
          fetchTrending('week'),
        ]);

        setMovies(popularMovies);
        popularMoviesCache.current = popularMovies;

        // Use trending for hero slider (better variety)
        const heroItems = trendingData
          .filter((item: MediaItem) => item.backdrop_path && (item.media_type === 'movie' || item.media_type === 'tv'))
          .slice(0, APP_CONFIG.sliderCount);
        setSliderMovies(heroItems);

        initialLoadDone.current = true;
      } catch (err) {
        setError('Failed to load content. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchPopularMovies, fetchTrending]);

  // Fetch trailer separately once speed is tested (prevents initial flicker)
  useEffect(() => {
    if (!speedTested || !isFast || sliderMovies.length === 0 || trailerKey) return;

    const loadTrailer = async () => {
      const firstItem = sliderMovies[0];
      const trailer = await fetchTrailer(firstItem.media_type, firstItem.id);
      if (trailer) {
        setTrailerKey(trailer);
        setTimeout(() => setShowTrailer(true), 3000);
      }
    };

    loadTrailer();
  }, [speedTested, isFast, sliderMovies, fetchTrailer, trailerKey]);

  // Search handler with debounce
  useEffect(() => {
    // Skip on initial load
    if (loading) return;

    const searchTimer = setTimeout(async () => {
      if (searchTerm.trim()) {
        try {
          const results = await searchMulti(searchTerm);
          setMovies(results);
        } catch (err) {
          console.error('Search error:', err);
        }
      } else if (popularMoviesCache.current.length > 0) {
        // Reset to cached popular movies when search is cleared
        setMovies(popularMoviesCache.current);
      }
    }, APP_CONFIG.searchDebounce);

    return () => clearTimeout(searchTimer);
  }, [searchTerm, searchMulti, loading]);

  // Auto-advance slider
  useEffect(() => {
    if (prefersReducedMotion || sliderMovies.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % sliderMovies.length);
    }, APP_CONFIG.sliderInterval);

    return () => clearInterval(interval);
  }, [sliderMovies.length, prefersReducedMotion]);

  // Navigation handlers
  const handlePrevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + sliderMovies.length) % sliderMovies.length);
    setShowTrailer(false);
  }, [sliderMovies.length]);

  const handleNextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % sliderMovies.length);
    setShowTrailer(false);
  }, [sliderMovies.length]);

  const handleMediaClick = useCallback((id: number, type: 'movie' | 'tv') => {
    navigate(`/media/${type}/${id}`);
  }, [navigate]);

  // Current slide data
  const currentMovie = sliderMovies[currentSlide];

  if (error) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary-500 rounded-full font-semibold hover:bg-primary-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950">
      {/* Header */}
      <Header
        onSearch={setSearchTerm}
        searchValue={searchTerm}
        showSearch={true}
        transparent={!searchTerm && sliderMovies.length > 0}
      />

      {/* Hero Slider */}
      {!searchTerm && (
        <section className="relative hero-slider -mt-16 sm:-mt-18 lg:-mt-20 xl:-mt-24 2xl:-mt-28">
          {loading ? (
            <HeroSkeleton />
          ) : (
            <>
              {/* Slides */}
              {sliderMovies.map((movie, index) => {
                const isActive = index === currentSlide;
                const movieBackdrop = `${IMAGE_SIZES.backdrop.original}${movie.backdrop_path}`;

                return (
                  <div
                    key={movie.id}
                    className={`hero-slide ${isActive ? 'active' : ''}`}
                  >
                    {/* Background */}
                    <div className="absolute inset-0">
                      {/* Show trailer if available and active */}
                      {isActive && showTrailer && trailerKey && isFast && !isMobileOrTablet ? (
                        <iframe
                          src={`${YOUTUBE_CONFIG.embedBase}${trailerKey}?autoplay=1&mute=1&controls=0&loop=1&playlist=${trailerKey}&modestbranding=1&rel=0&showinfo=0`}
                          className="w-full h-full object-cover scale-125"
                          style={{ pointerEvents: 'none' }}
                          allow="autoplay; encrypted-media"
                          title="Trailer"
                        />
                      ) : (
                        <img
                          src={movieBackdrop}
                          alt={getMediaTitle(movie)}
                          className={`w-full h-full object-cover ${!prefersReducedMotion && isActive ? 'animate-zoom-out' : ''}`}
                        />
                      )}
                    </div>

                    {/* Gradient Overlays */}
                    <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-950/60 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-950/40 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-b from-dark-950/50 via-transparent to-transparent" />
                  </div>
                );
              })}

              {/* Content - Always on top */}
              {currentMovie && (
                <div className="hero-content">
                  <div className="space-y-4 sm:space-y-5 lg:space-y-6 hero-content-inner">
                    {/* Media Type Badge */}
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 rounded-full text-xs sm:text-sm font-semibold
                        bg-gradient-to-r from-primary-500 to-rose-500 text-white">
                        {currentMovie.media_type === 'tv' ? 'TV Series' : 'Movie'}
                      </span>
                      {currentMovie.vote_average > 0 && (
                        <span className="flex items-center gap-1 text-primary-400 font-semibold">
                          <FontAwesomeIcon icon={faStar} />
                          <span>{currentMovie.vote_average.toFixed(1)}</span>
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl 
                      font-black leading-tight tracking-tight
                      drop-shadow-2xl">
                      {getMediaTitle(currentMovie)}
                    </h1>

                    {/* Overview */}
                    <p className="text-sm sm:text-base lg:text-lg xl:text-xl 2xl:text-2xl
                      text-gray-300 line-clamp-2 sm:line-clamp-3 max-w-2xl
                      drop-shadow-lg">
                      {currentMovie.overview}
                    </p>

                    {/* Buttons */}
                    <div className="flex flex-wrap gap-3 sm:gap-4 pt-2">
                      <button
                        onClick={() => handleMediaClick(currentMovie.id, currentMovie.media_type)}
                        className="flex items-center gap-2 sm:gap-3 
                          px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5
                          bg-white text-dark-950 rounded-full
                          font-bold text-sm sm:text-base lg:text-lg
                          transform transition-all duration-300
                          hover:scale-105 hover:shadow-glow-lg
                          focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500"
                      >
                        <FontAwesomeIcon icon={faPlay} />
                        <span>Play Now</span>
                      </button>

                      <button
                        onClick={() => handleMediaClick(currentMovie.id, currentMovie.media_type)}
                        className="flex items-center gap-2 sm:gap-3
                          px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5
                          bg-white/20 backdrop-blur-sm text-white rounded-full
                          font-bold text-sm sm:text-base lg:text-lg
                          border border-white/30
                          transform transition-all duration-300
                          hover:bg-white/30 hover:scale-105
                          focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
                      >
                        <FontAwesomeIcon icon={faInfoCircle} />
                        <span>More Info</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Slider Controls */}
              <div className="absolute bottom-8 sm:bottom-12 lg:bottom-16 right-4 sm:right-8 lg:right-12 
                flex items-center gap-4 z-20">
                {/* Navigation Arrows */}
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevSlide}
                    className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14
                      rounded-full bg-white/10 backdrop-blur-sm
                      text-white border border-white/20
                      flex items-center justify-center
                      transition-all duration-300
                      hover:bg-white/20 hover:scale-110
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    aria-label="Previous slide"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <button
                    onClick={handleNextSlide}
                    className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14
                      rounded-full bg-white/10 backdrop-blur-sm
                      text-white border border-white/20
                      flex items-center justify-center
                      transition-all duration-300
                      hover:bg-white/20 hover:scale-110
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    aria-label="Next slide"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>

              {/* Slide Indicators */}
              <div className="absolute bottom-8 sm:bottom-12 lg:bottom-16 left-1/2 -translate-x-1/2
                flex gap-2 sm:gap-3 z-20">
                {sliderMovies.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentSlide(index);
                      setShowTrailer(false);
                    }}
                    className={`h-1 sm:h-1.5 rounded-full transition-all duration-500
                      ${index === currentSlide
                        ? 'w-8 sm:w-12 bg-primary-500'
                        : 'w-2 sm:w-3 bg-white/40 hover:bg-white/60'
                      }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Main Content */}
      <main className={`relative z-10 ${searchTerm ? 'pt-8' : 'mt-4 sm:-mt-12 lg:-mt-20'}`}>
        <div className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">

          {/* Section Title */}
          <div className="mb-6 sm:mb-8 lg:mb-10">
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold">
              {searchTerm ? (
                <>
                  Search Results for{' '}
                  <span className="text-primary-500">"{searchTerm}"</span>
                </>
              ) : (
                <>
                  <span className="text-gradient">Popular</span> Movies & TV Shows
                </>
              )}
            </h2>
          </div>

          {/* Media Grid */}
          <div className="media-grid">
            {loading ? (
              <MediaCardSkeleton count={20} size="full" />
            ) : movies.length > 0 ? (
              movies.map((item, index) => (
                <MediaCard
                  key={item.id}
                  item={item}
                  index={index}
                  size="full"
                  showRating={true}
                  showType={!!searchTerm}
                />
              ))
            ) : (
              <div className="col-span-full text-center py-20">
                <p className="text-gray-400 text-lg">No results found for "{searchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-primary-500 hover:text-primary-400 font-semibold"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default JustFlix;
